import React, { useState } from 'react';
import { X, Music, Upload, Trash2, Image as ImageIcon, Bold, Italic, List, Link as LinkIcon, Heading, AlignLeft } from 'lucide-react';

const API_BASE_URL = 'https://artdownload2-production.up.railway.app';

const AudioLessonsModal = ({ 
  showModal, 
  setShowModal, 
  formData, 
  setFormData, 
  editingItem, 
  loading, 
  handleSave,
  categories 
}) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Функции для форматирования текста
  const formatText = (format) => {
    const textarea = document.querySelector('textarea[name="description"]');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.description.substring(start, end);
    let formattedText = '';
    let newCursorPos = 0;

    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        newCursorPos = start + 2;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        newCursorPos = start + 1;
        break;
      case 'heading':
        formattedText = `## ${selectedText}`;
        newCursorPos = start + 3;
        break;
      case 'list':
        formattedText = selectedText.split('\n').map(line => `- ${line}`).join('\n');
        newCursorPos = start + 2;
        break;
      case 'link':
        const url = prompt('Введите URL ссылки:', 'https://');
        if (url) {
          formattedText = `[${selectedText || 'текст ссылки'}](${url})`;
          newCursorPos = start + 1;
        } else {
          return;
        }
        break;
      default:
        return;
    }

    const newText = formData.description.substring(0, start) + formattedText + formData.description.substring(end);
    setFormData(prev => ({ ...prev, description: newText }));

    // Восстанавливаем позицию курсора
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + (selectedText ? formattedText.length : newCursorPos),
        start + (selectedText ? formattedText.length : newCursorPos)
      );
    }, 0);
  };

  // Функция для конвертации Markdown в HTML (упрощенная)
  const renderMarkdown = (text) => {
    if (!text) return '<p class="text-gray-400 italic">Нет описания</p>';

    let html = text
      // Заголовки
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-white mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-white mt-4 mb-2">$1</h1>')
      // Жирный текст
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold text-white">$1</strong>')
      // Курсив
      .replace(/\*(.*?)\*/gim, '<em class="italic text-white">$1</em>')
      // Списки
      .replace(/^- (.*$)/gim, '<li class="ml-4 text-white">• $1</li>')
      // Ссылки
      .replace(/\[(.*?)\]\((.*?)\)/gim, '<a href="$2" target="_blank" class="text-cyan-400 hover:text-cyan-300 underline">$1</a>')
      // Переносы строк
      .replace(/\n/gim, '<br />');

    // Оборачиваем списки в ul
    if (html.includes('<li')) {
      html = html.replace(/(<li.*?>.*?<\/li>)+/gim, match => `<ul class="list-disc pl-5 mb-4">${match}</ul>`);
    }

    // Разбиваем на параграфы
    const paragraphs = html.split('<br /><br />');
    html = paragraphs.map(p => {
      if (!p.startsWith('<h') && !p.startsWith('<ul')) {
        return `<p class="text-gray-300 mb-2">${p}</p>`;
      }
      return p;
    }).join('');

    return html;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите файл изображения (PNG, JPG, GIF)');
      return;
    }

    setUploadingImage(true);
    setUploadError(null);
    
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      console.log('Отправка изображения на сервер...', file.name);

      const response = await fetch(`${API_BASE_URL}/upload?folder=images`, {
        method: 'POST',
        body: formDataObj,
        headers: {
          'Accept': 'application/json',
        }
      });

      console.log('Статус ответа:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Ошибка сервера:', errorText);
        throw new Error(`Ошибка сервера: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Ответ сервера:', data);
      
      // Проверяем разные возможные форматы ответа
      let imageUrl;
      if (data.url) {
        imageUrl = data.url;
      } else if (data.fileUrl) {
        imageUrl = data.fileUrl;
      } else if (data.path) {
        // Если сервер возвращает относительный путь
        imageUrl = `${API_BASE_URL}${data.path}`;
      } else if (data.filename) {
        imageUrl = `${API_BASE_URL}/files/${data.filename}`;
      } else {
        throw new Error('Неизвестный формат ответа сервера');
      }

      console.log('Используемый URL изображения:', imageUrl);
      setFormData(prev => ({ ...prev, image: imageUrl }));
      
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      setUploadError(`Не удалось загрузить изображение: ${error.message}`);
      
      // Альтернативный вариант: читаем файл как data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;
        setFormData(prev => ({ ...prev, image: base64Image }));
        alert('Изображение загружено локально (base64). При сохранении урока изображение будет храниться в виде base64 строки.');
      };
      reader.readAsDataURL(file);
      
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const audioFiles = files.filter(file => file.type.startsWith('audio/'));
    if (audioFiles.length === 0) {
      alert('Пожалуйста, выберите аудио файлы (MP3, WAV, AAC)');
      return;
    }

    setUploadingAudio(true);
    setUploadError(null);
    
    try {
      const newAudioFiles = [...formData.audioFiles];
      
      for (const file of audioFiles) {
        try {
          const formDataObj = new FormData();
          formDataObj.append('file', file);

          const response = await fetch(`${API_BASE_URL}/upload?folder=audio`, {
            method: 'POST',
            body: formDataObj,
            headers: {
              'Accept': 'application/json',
            }
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Ошибка загрузки ${file.name}:`, errorText);
            throw new Error(`Ошибка сервера: ${response.status}`);
          }

          const data = await response.json();
          
          // Проверяем разные возможные форматы ответа
          let audioUrl;
          if (data.url) {
            audioUrl = data.url;
          } else if (data.fileUrl) {
            audioUrl = data.fileUrl;
          } else if (data.path) {
            audioUrl = `${API_BASE_URL}${data.path}`;
          } else if (data.filename) {
            audioUrl = `${API_BASE_URL}/files/${data.filename}`;
          } else {
            throw new Error('Неизвестный формат ответа сервера');
          }

          newAudioFiles.push({
            name: file.name,
            url: audioUrl,
            type: file.type,
            size: file.size
          });
          
        } catch (fileError) {
          console.error(`Ошибка загрузки файла ${file.name}:`, fileError);
          alert(`Не удалось загрузить файл "${file.name}". Попробуйте другой файл.`);
        }
      }

      if (newAudioFiles.length > formData.audioFiles.length) {
        setFormData(prev => ({ ...prev, audioFiles: newAudioFiles }));
        alert(`Успешно загружено ${newAudioFiles.length - formData.audioFiles.length} файлов`);
      }
      
    } catch (error) {
      console.error('Общая ошибка загрузки аудио файлов:', error);
      setUploadError(`Ошибка загрузки аудио файлов: ${error.message}`);
    } finally {
      setUploadingAudio(false);
    }
  };

  const removeAudioFile = (index) => {
    const newAudioFiles = formData.audioFiles.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, audioFiles: newAudioFiles }));
  };

  const handleManualImageUrl = () => {
    const url = prompt('Введите URL изображения:');
    if (url && url.trim()) {
      setFormData(prev => ({ ...prev, image: url.trim() }));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Проверяем URL изображения
  const getImageUrl = (url) => {
    if (!url) return url;
    
    // Если это data URL (base64)
    if (url.startsWith('data:')) {
      return url;
    }
    
    // Если URL начинается с / - добавляем базовый URL
    if (url.startsWith('/')) {
      return `${API_BASE_URL}${url}`;
    }
    
    // Если URL уже полный
    if (url.startsWith('http')) {
      return url;
    }
    
    // По умолчанию считаем относительным путем и добавляем базовый URL
    return `${API_BASE_URL}/${url}`;
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-900 opacity-90"></div>
        </div>

        <div className="inline-block align-bottom bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-gray-700">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {editingItem ? 'Редактировать аудио урок' : 'Создать аудио урок'}
                </h3>
                <p className="text-sm text-gray-400 mt-1">Заполните информацию об аудио уроке</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {uploadError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
                {uploadError}
              </div>
            )}

            <div className="space-y-6">
              {/* Основные поля */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Название *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="Введите название аудио урока"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Категория
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Изображение
                  </label>
                  <div className="relative">
                    {formData.image ? (
                      <div className="relative">
                        <div className="w-full h-48 rounded-xl border-2 border-emerald-500/30 overflow-hidden bg-gray-700/50">
                          <img
                            src={getImageUrl(formData.image)}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Ошибка загрузки изображения:', formData.image);
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              parent.innerHTML = `
                                <div class="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                  <svg class="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                  </svg>
                                  <p class="text-sm">Изображение недоступно</p>
                                  <p class="text-xs mt-1 text-center px-2">${formData.image.substring(0, 50)}...</p>
                                </div>
                              `;
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                          className="absolute top-2 right-2 p-2 bg-red-500/80 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-600/50 rounded-xl cursor-pointer hover:border-emerald-500/50 transition-colors bg-gray-700/30">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-12 h-12 mb-3 text-gray-500" />
                            <p className="mb-2 text-sm text-gray-400">
                              <span className="font-semibold">Нажмите для загрузки</span>
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handleManualImageUrl}
                          className="mt-2 w-full py-2 text-sm text-cyan-400 hover:text-cyan-300 border border-cyan-400/30 rounded-lg hover:bg-cyan-400/10 transition-colors"
                        >
                          Или введите URL вручную
                        </button>
                      </>
                    )}
                    {uploadingImage && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 rounded-xl">
                        <div className="text-emerald-400 flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mb-2"></div>
                          Загрузка...
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Описание с редактором */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Описание *
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                        showPreview 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : 'text-gray-400 hover:text-white border border-gray-600/50'
                      }`}
                    >
                      {showPreview ? 'Редактор' : 'Предпросмотр'}
                    </button>
                  </div>
                </div>

                {!showPreview ? (
                  <>
                    {/* Панель инструментов */}
                    <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-700/30 rounded-lg border border-gray-600/30">
                      <button
                        type="button"
                        onClick={() => formatText('bold')}
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-600/50 rounded-lg transition-colors"
                        title="Жирный"
                      >
                        <Bold className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => formatText('italic')}
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-600/50 rounded-lg transition-colors"
                        title="Курсив"
                      >
                        <Italic className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => formatText('heading')}
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-600/50 rounded-lg transition-colors"
                        title="Заголовок"
                      >
                        <Heading className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => formatText('list')}
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-600/50 rounded-lg transition-colors"
                        title="Список"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => formatText('link')}
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-600/50 rounded-lg transition-colors"
                        title="Ссылка"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                      <div className="border-l border-gray-600/50 mx-1 h-6 self-center"></div>
                      <span className="text-xs text-gray-500 self-center">
                        Используйте Markdown разметку
                      </span>
                    </div>

                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="8"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-y font-mono text-sm"
                      placeholder="Введите описание аудио урока с использованием Markdown разметки:

**жирный текст**
*курсив*
## Заголовок
- элемент списка
[текст ссылки](https://example.com)"
                    />
                  </>
                ) : (
                  <div className="w-full min-h-[200px] px-4 py-3 bg-gray-700/30 border border-gray-600/50 rounded-xl text-white prose prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(formData.description) }} />
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  Поддерживается Markdown разметка: **жирный**, *курсив*, ## заголовок, - список, [ссылка](url)
                </p>
              </div>

              {/* Аудио файлы */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Аудио файлы
                </label>
                <div className="space-y-4">
                  <label className="flex flex-col items-center justify-center w-full p-8 border-2 border-dashed border-gray-600/50 rounded-xl cursor-pointer hover:border-emerald-500/50 transition-colors bg-gray-700/30">
                    <div className="flex flex-col items-center justify-center">
                      <Music className="w-12 h-12 mb-3 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-400">
                        <span className="font-semibold">Нажмите для загрузки аудио</span>
                      </p>
                      <p className="text-xs text-gray-500">MP3, WAV, AAC</p>
                      <p className="text-xs text-gray-400 mt-2">Можно выбрать несколько файлов</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="audio/*"
                      multiple
                      onChange={handleAudioUpload}
                      disabled={uploadingAudio}
                    />
                  </label>

                  {uploadingAudio && (
                    <div className="text-center text-emerald-400 flex flex-col items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400 mb-2"></div>
                      Загрузка аудио файлов...
                    </div>
                  )}

                  {formData.audioFiles.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">
                        Загруженные файлы ({formData.audioFiles.length})
                      </p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {formData.audioFiles.map((audio, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <Music className="w-5 h-5 text-emerald-400" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">{audio.name}</p>
                                <p className="text-xs text-gray-400">
                                  {formatFileSize(audio.size)}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAudioFile(index)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Премиум */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="premium"
                  name="premium"
                  checked={formData.premium}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500 focus:ring-2"
                />
                <label htmlFor="premium" className="ml-3 text-sm text-gray-300">
                  Премиум урок
                </label>
              </div>
            </div>
          </div>

          {/* Кнопки */}
          <div className="px-6 py-5 bg-gray-800/50 border-t border-gray-700/50">
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 py-3 text-gray-400 hover:text-white font-medium rounded-xl hover:bg-gray-700/50 transition-colors border border-gray-600/50"
                disabled={loading}
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                disabled={loading || uploadingImage || uploadingAudio}
                className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-500 text-white font-medium rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Music className="w-5 h-5" />
                    {editingItem ? 'Сохранить изменения' : 'Создать аудио урок'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioLessonsModal;